import SidePanelHeader from "../components/SidePanelHeader"
import SidePanelBreadCrumb from "../components/SidePanelBreadCrumb"
import SidePanelAuditTrail from "../components/SidePanelAuditTrail"
import { EntityNames } from "../../../../../enums/EntityNames"
import { useNavigate } from "react-router"
import * as Navigation from "../../../../../modules/Navigation";
import SidePanelInfo from "../components/SidePanelInfo"
import SidePanelNonSensitiveInfo from "../components/SidePanelNonSensitiveInfo"
import { useEffect, useRef, useState } from "react"
import { httpGet, httpPost } from "../../../../../modules/Backend"
import { hasPermission } from "../../../../../modules/auth/Permissions/PermissionModule";
import { PermissionConcerns, PermissionTypes } from "../../../../../modules/auth/Permissions/PermissionStrings";
import { usePermissions } from "../../../../../modules/auth/Permissions/PermissionContext"
import { PackingBoxSchema } from "../../../entityComponents/Schemas/PackingBoxSchema"
import { hasVisibleChildren } from "../../../entityComponents/Schema"
import { Button } from "react-aria-components"
import ConfirmModal, { ConfirmType } from "../../../Modal/ConfirmModal"
import { modalSignal } from "../../../Modal/ModalContainer"
import { HttpStatusCode } from "axios"
import Form from "../../../entityComponents/Form/Form"
import { globalCanSubmitSignal, globalDeletionSignal, globalLoadingSignal, globalRealTimeEventSignal, globalUpdateResultsSignal, globalSubmittingSignal, headerWidgetSignal } from "../../Layout"
import SidePanelPackingBoxRanges, { IPackingBoxRange } from "../components/SidePanelPackingBoxRanges"
import { useSignals } from "@preact/signals-react/runtime"
import PrintPackingBoxLabel from "../../../print/PrintPackingBoxLabel"
import { OuterCubeFullness } from "./SidePanelOuterCube"
import SidePanelPackingBoxCases, { IPackingBoxCase } from "../components/SidePanelPackingBoxCases"
import ExistingCasesModal from "../../../Modal/ExistingCasesModal"
import CreateCaseWithPartCasesModal from "../../../Modal/CreateCaseWithPartCasesModal"
import { StatusDto } from "../../../../../enums/Domain/Status"
import { PackingBoxStatusSmartEnum } from "../../../../../enums/Domain/PackingBoxStatusSmartEnum"

enum PackingBoxInteractionMode {
    View = 0,
    EditPackingBox = 1,
    AddRange = 2,
    EditRange = 3,
    AddCase = 4,
    EditCase = 5,
    Print = 6,
}

export default function SidePanelPackingBox({ data, onViewPage }: any) {
    useSignals()

    const navigate = useNavigate();
    const [location, setLocation] = useState<string>("");
    const permissionsContext = usePermissions();
    const [mode, setMode] = useState<PackingBoxInteractionMode>(PackingBoxInteractionMode.View);
    const [editingRangeId, setEditingRangeId] = useState<string | undefined>(undefined);
    const [editingCaseId, setEditingCaseId] = useState<string | undefined>(undefined);
    const [editingEntityName, setEditingEntityName] = useState<string | undefined>(undefined);
    const [rangeData, setRangeData] = useState<IPackingBoxRange | undefined>(undefined);
    const [caseData, setCaseData] = useState<IPackingBoxCase | undefined>(undefined);
    const [status, setStatus] = useState<StatusDto | undefined>(undefined);
    const [volume, setVolume] = useState<string | undefined>(undefined);
    const [belongsTo, setBelongsTo] = useState<string | undefined>(undefined);
    const [typeName, setTypeName] = useState<string | undefined>(undefined);

    function viewEntity() {
        // Only navigate if there are visible children
        if (hasVisibleChildren(data.entityName, permissionsContext)) {
            Navigation.view.navigate(navigate, data.entityName, data.id, data)
        }
    }

    function viewEntityAndTab(tabName?: string) {
        // Only navigate if there are visible children
        if (hasVisibleChildren(data.entityName, permissionsContext)) {
            const indexOfTab = PackingBoxSchema.children?.findIndex(x => x.entityName == tabName) ?? 0
            Navigation.view.navigate(navigate, data.entityName, data.id, data, indexOfTab)
        }
    }

    async function getLocation() {
        const result = await httpGet(`api/${EntityNames.packingBox}/location?id=${data.id}`, true)
        if (result?.data?.breadcrumb) {
            setLocation(result.data.breadcrumb)
        }
    }

    async function onBoxEdited(data: Record<string, any>) {
        if (data && data.newOuterCubePercentageFull !== null && data.newOuterCubePercentageFull !== undefined) {
            headerWidgetSignal.value = <OuterCubeFullness amount={Number(data.newOuterCubePercentageFull)} />
        }
        if (data) {
            setVolume(data.volume);
            setBelongsTo(data.belongsTo);
            setTypeName(data.typeName);
            globalUpdateResultsSignal.value = {
                id: data.id,
                updateChanges: {
                    belongsTo: data.belongsTo,
                    typeName: data.typeName,
                    volume: data.volume,
                    // Annoyingly - in aggregate containers list, volume of a pb is refered to loosely as type, so we need
                    // to send another property for consumption of the aggregate list as well as volume property 
                    type: data.volume,
                }
            }
        }
        setMode(PackingBoxInteractionMode.View);
    }

    function onRangeAdded(rangeData: any) {
        setMode(PackingBoxInteractionMode.View);
        const data = {
            id: rangeData.id,
            yearStart: rangeData.yearStart,
            yearEnd: rangeData.yearEnd,
            displayName: `${rangeData.startCode} > ${rangeData.endCode}`,
            prefix: rangeData.prefix
        };
        setRangeData(data);

        // Update packing box type in the list if provided
        if (rangeData.packingBoxId && rangeData.packingBoxTypeName) {
            globalUpdateResultsSignal.value = {
                id: rangeData.packingBoxId,
                updateChanges: {
                    typeName: rangeData.packingBoxTypeName
                }
            };
        }
    }

    function handleAddRange() {
        setMode(PackingBoxInteractionMode.AddRange);
    }

    function onRangeEdited(rangeData: any) {
        setMode(PackingBoxInteractionMode.View);
        setEditingRangeId(undefined);
        const data = {
            id: rangeData.id,
            yearStart: rangeData.yearStart,
            yearEnd: rangeData.yearEnd,
            displayName: `${rangeData.startCode} > ${rangeData.endCode}`,
            prefix: rangeData.prefix
        };
        setRangeData(data);

        // Update packing box type in the list if provided
        if (rangeData.packingBoxId && rangeData.packingBoxTypeName) {
            globalUpdateResultsSignal.value = {
                id: rangeData.packingBoxId,
                updateChanges: {
                    typeName: rangeData.packingBoxTypeName
                }
            };
        }
    }

    function onRangeEditClicked(id: string) {
        setEditingRangeId(id);
        setMode(PackingBoxInteractionMode.EditRange);
    }

    const [caseItemsToAdd, setCaseItemsToAdd] = useState<IPackingBoxCase[] | undefined>(undefined);

    function onCaseAdded(caseData: any) {
        // Add the case itself
        const newCaseData: IPackingBoxCase = {
            id: caseData.id,
            code: caseData.code,
            year: caseData.year,
            prefix: caseData.prefix,
            entityName: EntityNames.cases
        };
        setCaseData(newCaseData);

        const partCasesToAdd: IPackingBoxCase[] = [];
        if (caseData.specimens && Array.isArray(caseData.specimens) && caseData.specimens.length > 0) {
            caseData.specimens.forEach((specimen: any) => {
                const partCaseItem: IPackingBoxCase = {
                    id: specimen.id,
                    code: `${caseData.code} - ${specimen.code}`, // Combined code format
                    year: caseData.year,
                    prefix: caseData.prefix,
                    entityName: EntityNames.specimens
                };
                partCasesToAdd.push(partCaseItem);
            });
        }

        // Set part cases to add (will be handled by SidePanelList useEffect)
        if (partCasesToAdd.length > 0) {
            setCaseItemsToAdd(partCasesToAdd);
            // Clear after processing to prevent re-adding
            setTimeout(() => {
                setCaseItemsToAdd(undefined);
            }, 0);
        }

        const rangeData = {
            id: caseData.rangeId,
            yearStart: caseData.year,
            yearEnd: caseData.year,
            displayName: `${caseData.code} > ${caseData.code}`,
            prefix: caseData.prefix
        };
        setRangeData(rangeData);

        // Update packing box type in the list if provided
        if (caseData.packingBoxId && caseData.packingBoxTypeName) {
            globalUpdateResultsSignal.value = {
                id: caseData.packingBoxId,
                updateChanges: {
                    typeName: caseData.packingBoxTypeName
                }
            };
        }
    }

    function handleAddCase() {
        // Show the inline form first to collect case data
        setMode(PackingBoxInteractionMode.AddCase);
    }

    function onCaseEdited(caseData: any) {
        setMode(PackingBoxInteractionMode.View);
        setEditingCaseId(undefined);
        setEditingEntityName(undefined);
        const data = {
            id: caseData.id,
            code: caseData.code,
            year: caseData.year,
            prefix: caseData.prefix
        };
        setCaseData(data);

        // Update packing box type in the list if provided
        if (caseData.packingBoxId && caseData.packingBoxTypeName) {
            globalUpdateResultsSignal.value = {
                id: caseData.packingBoxId,
                updateChanges: {
                    typeName: caseData.packingBoxTypeName
                }
            };
        }
    }

    function onCaseEditClicked(id: string, entityName?: string) {
        setEditingCaseId(id);
        setEditingEntityName(entityName);
        setMode(PackingBoxInteractionMode.EditCase);
    }

    async function handleCaseRemoved(item: IPackingBoxCase) {
        // After a case is deleted, fetch the updated packing box type and update the list
        await updatePackingBoxTypeAfterDeletion();
    }

    async function handleRangeRemoved(item: IPackingBoxRange) {
        // After a range is deleted, fetch the updated packing box type and update the list
        await updatePackingBoxTypeAfterDeletion();
    }

    async function updatePackingBoxTypeAfterDeletion() {
        if (data?.id) {
            try {
                const result = await httpGet(`api/${EntityNames.packingBox}/get?id=${data.id}`);
                if (result?.data?.typeName) {
                    globalUpdateResultsSignal.value = {
                        id: data.id,
                        updateChanges: {
                            typeName: result.data.typeName
                        }
                    };
                    // Also update local state
                    setTypeName(result.data.typeName);
                }
            } catch (error) {
                console.error('Failed to fetch updated packing box type after deletion:', error);
            }
        }
    }

    function getContextualInfo() {
        switch (mode) {
            case PackingBoxInteractionMode.EditPackingBox:
                return "Editing existing packing box";
            case PackingBoxInteractionMode.AddRange:
                return "Adding new range";
            case PackingBoxInteractionMode.EditRange:
                return "Editing existing range";
            case PackingBoxInteractionMode.AddCase:
                return "Returning a case";
            case PackingBoxInteractionMode.EditCase:
                return editingEntityName === EntityNames.cases ? "Editing existing case" : "Editing existing part case";
        }
        return undefined;
    }

    function handleSubmissionCancelled() {
        setMode(PackingBoxInteractionMode.View);
    }

    function resetSubmittingState() {
        globalSubmittingSignal.value = { isBusy: false };
        globalCanSubmitSignal.value = { allowed: true };
    }

    async function checkRangeSize(submit: Function, formData: any) {
        const params = new URLSearchParams();
        params.set("rangeStart_Original", formData.rangeStart_Original);
        params.set("rangeEnd_Original", formData.rangeEnd_Original);
        const rangeCheck = await httpGet(`api/${EntityNames.ranges}/checkRangeSize?${params.toString()}`);
        if (rangeCheck.status === HttpStatusCode.Ok) {
            if (rangeCheck.data.requiresIntervention) {
                modalSignal.value = <ConfirmModal
                    heading={`Warning the range size exceeds ${rangeCheck.data.maxSize}`}
                    information="Are you sure you want to continue?"
                    ContinueFunc={async () => { await checkOverlappingRanges(submit, formData) }}
                    cancelFunc={resetSubmittingState}
                    autoClose={false} />
            } else {
                await checkOverlappingRanges(submit, formData);
            }
        }
    }

    async function checkOverlappingRanges(submit: Function, formData: any) {
        modalSignal.value = undefined;
        const params = new URLSearchParams();
        if (editingRangeId) {
            params.set("id", editingRangeId);
        }
        params.set("startCode", formData.rangeStart_Original);
        params.set("endCode", formData.rangeEnd_Original);
        params.set("yearStart", formData.yearStart);
        params.set("yearEnd", formData.yearEnd);
        params.set("parentId", data.id);
        const rangeCheck = await httpGet(`api/${EntityNames.ranges}/checkOverlappingRanges?${params.toString()}`);
        if (rangeCheck.status === HttpStatusCode.Ok) {
            if (rangeCheck.data?.overlaps?.length > 0) {
                const rangesDescriptor = rangeCheck.data?.overlaps?.length === 1 ? "" : "s";
                modalSignal.value = <ConfirmModal
                    heading={`This range overlaps with at least ${rangeCheck.data?.overlaps?.length} other range${rangesDescriptor}`}
                    information="Are you sure you want to continue?"
                    ContinueFunc={async () => { await submit() }}
                    cancelFunc={resetSubmittingState} />
            } else {
                await submit();
            }
        } else {
            globalLoadingSignal.value = { isBusy: false };
            globalSubmittingSignal.value = { isBusy: false };
            globalCanSubmitSignal.value = { allowed: true };
        }
    }

    async function findMatchingCases(submit: Function, formData: any, clearValues: Function) {
        modalSignal.value = undefined;
        const params = new URLSearchParams();
        params.set("packingBoxId", data.id);
        params.set("code", formData.code);
        params.set("year", formData.year);
        params.set("quantity", formData.quantity);
        params.set("accountId", formData.accountId);
        params.set("caseMaterialTypeId", formData.caseMaterialTypeId);
        const returnSearch = await httpGet(`api/${EntityNames.cases}/returnSearch?${params.toString()}`);
        if (returnSearch.status === HttpStatusCode.Ok) {
            if (returnSearch.data?.cases?.length > 0) {
                modalSignal.value = <ExistingCasesModal
                    heading={"Existing Cases"}
                    information={`We found ${returnSearch.data?.cases?.length} case${returnSearch.data?.cases?.length === 1 ? '' : 's'} matching your input criteria. Select a case to return, and / or return individual part cases.`}
                    ContinueFunc={(selectedCase: any) => returnExistingCase(selectedCase, formData.quantity)}
                    cancelFunc={resetSubmittingState}
                    existingCases={returnSearch.data?.cases}
                    clearValues={clearValues}
                    packingBoxId={data.id} />
            } else {
                await confirmCaseCreation(submit, formData, clearValues);
            }
        }
    }

    async function confirmCaseCreation(submit: Function, formData: any, clearValues: Function) {
        modalSignal.value = <CreateCaseWithPartCasesModal
            packingBoxId={data.id}
            caseFormData={formData}
            onSuccess={(caseData) => {
                onCaseAdded(caseData);
                modalSignal.value = undefined;
                if (clearValues) {
                    clearValues();
                }
            }}
            onCancel={resetSubmittingState}
            clearValues={clearValues}
        />
    }

    async function returnExistingCase(caseId: string, quantity: number) {
        const payload = {
            id: caseId,
            packingBoxId: data.id,
            quantity: quantity
        };

        const returnSearch = await httpPost(`api/${EntityNames.cases}/returnExisting`, payload);
        if (returnSearch.status === HttpStatusCode.Ok) {
            onCaseAdded(returnSearch.data);
        }

        modalSignal.value = undefined;
        globalLoadingSignal.value = { isBusy: false };
        globalSubmittingSignal.value = { isBusy: false };
    }

    function renderEditPackingBoxForm() {
        return <div className="margin__top--s">
            <Form isCreate={false}
                entityName={data.entityName}
                entityId={data.id}
                parentId={data.parentId}
                parentEntityName={EntityNames.outerCubes}
                isInline={true}
                onSubmitCallback={onBoxEdited}
                onCancel={handleSubmissionCancelled} />
        </div>
    }

    function renderAddRangeForm() {
        return <div className="margin__top--s">
            <Form isCreate={true}
                entityName={EntityNames.ranges}
                parentEntityName={EntityNames.packingBox}
                parentId={data.id}
                preLoadedParentData={data}
                isInline={true}
                disableAddEditEvents={true}
                onSubmitCallback={onRangeAdded}
                submitFunction={checkRangeSize}
                onCancel={handleSubmissionCancelled} />
        </div>
    }

    function renderEditRangeForm() {
        return <div className="margin__top--s">
            <Form isCreate={false}
                entityName={EntityNames.ranges}
                parentEntityName={EntityNames.packingBox}
                parentId={data.id}
                preLoadedParentData={data}
                entityId={editingRangeId}
                isInline={true}
                disableAddEditEvents={true}
                onSubmitCallback={onRangeEdited}
                submitFunction={checkRangeSize}
                onCancel={handleSubmissionCancelled} />
        </div>
    }


    function renderAddCaseForm() {
        const clearFunc = (key: string, value: any) => key !== "accountId";
        return <div className="margin__top--s">
            <Form isCreate={true}
                entityName={EntityNames.cases}
                parentEntityName={EntityNames.packingBox}
                parentId={data.id}
                preLoadedParentData={data}
                isInline={true}
                disableAddEditEvents={true}
                submitFunction={findMatchingCases}
                onSubmitCallback={onCaseAdded}
                clearFunc={clearFunc}
                onCancel={handleSubmissionCancelled} />
        </div>
    }

    function renderEditCaseForm() {
        return <div className="margin__top--s">
            <Form isCreate={false}
                entityName={editingEntityName!}
                parentEntityName={EntityNames.packingBox}
                entityId={editingCaseId}
                parentId={data.id}
                preLoadedParentData={data}
                isInline={true}
                disableAddEditEvents={true}
                onSubmitCallback={onCaseEdited}
                onCancel={handleSubmissionCancelled} />
        </div>
    }

    useEffect(() => {
        if (!data) return
        setStatus(data.status);
        setTypeName(data.typeName);
        setVolume(data.volume ?? data.type);
        setBelongsTo(data.accountName ?? data.belongsTo);
        data.currentLocation ? setLocation(data.currentLocation) : getLocation();
        setMode(PackingBoxInteractionMode.View);
    }, [data]);

    useEffect(() => {
        const signal = globalUpdateResultsSignal.value;
        if (signal.updateChanges && signal.updateChanges.location && signal.id) {
            setLocation(signal.updateChanges.location);
        }
    }, [globalUpdateResultsSignal.value]);

    return (<>
        {data &&
            <>
                {!onViewPage && <SidePanelHeader descriptor="Packing Box" value={data.reference} statuses={status} onClick={(() => { viewEntity() })} />}
                <Actions data={data} mode={mode} pbRef={data.reference} setMode={setMode} contextualInfo={getContextualInfo()} status={status} setStatus={setStatus} onViewPage={onViewPage} />
                {mode === PackingBoxInteractionMode.EditPackingBox &&
                    renderEditPackingBoxForm()
                }
                {mode === PackingBoxInteractionMode.AddRange &&
                    renderAddRangeForm()
                }
                {mode === PackingBoxInteractionMode.AddCase &&
                    renderAddCaseForm()
                }
                {mode === PackingBoxInteractionMode.EditRange && editingRangeId &&
                    renderEditRangeForm()
                }
                {mode === PackingBoxInteractionMode.EditCase &&
                    renderEditCaseForm()
                }
                <div className={`${mode === PackingBoxInteractionMode.View ? '' : 'display--hidden'}`}>
                    <SidePanelInfo values={[
                        { label: "Status", value: onViewPage ? status : undefined },
                        { label: "Reference number", value: data.reference },
                        { label: "Account", value: belongsTo },
                        { label: "Contents", value: typeName },
                        { label: "Sequence number", value: data.sequenceNumber },
                        { label: "Type", value: volume }]}
                    />
                    <SidePanelBreadCrumb descriptor="Current Location" value={location} />
                    {!onViewPage && data && status && status.value === PackingBoxStatusSmartEnum.Packed.value && !data.isChargedFor && <SidePanelPackingBoxCases id={data.id}
                        editCaseFunction={onCaseEditClicked}
                        caseData={caseData}
                        itemsToAdd={caseItemsToAdd}
                        onAddNewClicked={handleAddCase}
                        onItemRemoved={handleCaseRemoved} />
                    }
                    {!onViewPage && data && status && status.value === PackingBoxStatusSmartEnum.Packed.value && data.isChargedFor && <SidePanelPackingBoxRanges id={data.id}
                        editRangeFunction={onRangeEditClicked}
                        rangeData={rangeData}
                        onAddNewClicked={handleAddRange}
                        onItemRemoved={handleRangeRemoved} />
                    }
                    {
                        !onViewPage &&
                        hasPermission(permissionsContext.permissions!, PermissionTypes.read, PermissionConcerns.Inventory_Audit_Trails) &&
                        (<SidePanelAuditTrail entityName={EntityNames.packingBox} id={data.id} viewFunction={() => viewEntityAndTab(EntityNames.audits)} />)
                    }
                    {hasPermission(permissionsContext.permissions!, PermissionTypes.read, PermissionConcerns.Non_PII_Notes) &&
                        <SidePanelNonSensitiveInfo descriptor="Notes" data={data} dataTarget="notes" />}
                </div>
            </>
        }
    </>)
}

function Actions({ data, mode, setMode, contextualInfo, status, setStatus, onViewPage }: { data: any, mode: PackingBoxInteractionMode, pbRef: string, setMode: Function, contextualInfo: string | undefined, status: StatusDto | undefined, setStatus: Function, onViewPage: boolean }) {
    const { permissions } = usePermissions();

    return (
        <div className="display--flex flex__wrap--wrap margin__top--s justifyContent--space-between">
            <div className="display--flex gap--s">
                {mode !== PackingBoxInteractionMode.View && contextualInfo && <p className="textDecoration--italics">{contextualInfo}</p>}
                {mode === PackingBoxInteractionMode.View && hasPermission(permissions!, PermissionTypes.write, PermissionConcerns.Manage_Packing_Box) && <EditButton setMode={setMode} />}
                {mode === PackingBoxInteractionMode.View && hasPermission(permissions!, PermissionTypes.read, PermissionConcerns.Packing_Box_Label) && <LabelButton entityId={data?.id} />}
            </div>
            <div className="display--flex  gap--s">
                {hasPermission(permissions!, PermissionTypes.write, PermissionConcerns.Dispose) &&
                    mode === PackingBoxInteractionMode.View &&
                    status && status.value !== PackingBoxStatusSmartEnum.Disposed.value &&
                    <DisposeButton id={data.id} entityName={data.entityName} setStatus={setStatus} onViewPage={onViewPage} />
                }
                {mode === PackingBoxInteractionMode.View && hasPermission(permissions!, PermissionTypes.write, PermissionConcerns.Manage_Packing_Box) && <DeleteButton id={data.id} entityName={data.entityName} />}
            </div>
        </div>
    )
}

function EditButton({ setMode }: { setMode: Function }) {

    async function handleEdit() {
        if (setMode) {
            setMode(PackingBoxInteractionMode.EditPackingBox);
        }
    }

    return (
        <>
            <Button isDisabled={globalSubmittingSignal.value.isBusy} className="button button--small button--primary button--with-icon" onPress={handleEdit}>
                <p className="button-text">Edit</p>
            </Button>
        </>
    )
}

function LabelButton({ entityId }: { entityId: string | undefined }) {
    return (
        <>
            {entityId && <PrintPackingBoxLabel packingBoxId={entityId} />}
        </>
    )
}

function DeleteButton({ id, entityName }: { id: string, entityName: string }) {
    async function deletePackingBox(id: string) {
        const payload = { id };
        const result = await httpPost(`api/${entityName}/delete`, payload);
        if (result.status === HttpStatusCode.Ok) {
            if (result.data) {
                headerWidgetSignal.value = <OuterCubeFullness amount={result.data.outerCubePercentageFull} />
            }
            globalDeletionSignal.value = { id };
        }
    }

    function handleDelete() {
        modalSignal.value = <ConfirmModal heading={`Delete packing box?`}
            information="This can disrupt packing box sequence numbers, which may result in labels needing to be reprinted. Shifted sequence numbers will not immediately show as changed in lists, but they will show for label printing. If you need to check the new sequences, you can do a full page refresh"
            ContinueFunc={() => deletePackingBox(id)}
            type={ConfirmType.Destructive} />
    }

    return (
        <Button isDisabled={globalSubmittingSignal.value.isBusy} className={"button button--small button--primary button--secondary--destructive button--with-icon"} onPress={handleDelete}>
            <p className="button-text">Delete</p>
        </Button>
    )
}

function DisposeButton({ id, entityName, setStatus, onViewPage }: { id: string, entityName: string, setStatus: Function, onViewPage: boolean }) {
    async function disposePackingBox(id: string) {
        const payload = { id };
        const result = await httpPost(`api/${entityName}/dispose`, payload);
        if (result.status === HttpStatusCode.Ok) {
            if (result.data) {
                if (onViewPage) {
                    window.location.reload();
                } else {
                    const disposedStatus = new StatusDto(PackingBoxStatusSmartEnum.Disposed.value, PackingBoxStatusSmartEnum.getName());
                    globalUpdateResultsSignal.value = {
                        id: result.data.id,
                        updateChanges: {
                            status: disposedStatus,
                            location: result.data.locationBreadcrumb
                        }
                    }
                    setStatus(disposedStatus);
                }
            }
        }
    }

    function handleDispose() {
        modalSignal.value = <ConfirmModal heading={`Dispose packing box?`}
            information="This action cannot be undone, are you sure you want to continue?"
            ContinueFunc={() => disposePackingBox(id)}
            type={ConfirmType.Destructive} />
    }

    return (
        <Button isDisabled={globalSubmittingSignal.value.isBusy} className={"button button--small button--secondary button--secondary--destructive button--with-icon"} onPress={handleDispose}>
            <p className="button-text">Dispose</p>
        </Button>
    )
}